import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal, Label } from "@/components/site/Motion";
import { PageHero } from "@/components/site/Sections";
import { useSettings } from "@/hooks/useSettings";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SERVICES } from "@/data/content";

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Our team will contact you shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(s.map_query || "Lucknow")}&output=embed`;

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Let's talk about your project."
        subtitle="Book a free consultation. Tell us about your plot, space or idea and our team will guide you through the next steps."
      />

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <Label>Reach us directly</Label>
            <div className="mt-8 space-y-7">
              <Info icon={MapPin} title="Visit us" lines={[s.address]} />
              <Info icon={Phone} title="Call us" lines={[s.phone_primary, s.phone_secondary]} testid="contact-phone" />
              <Info icon={Mail} title="Email" lines={[s.email]} />
              <Info icon={Clock} title="Working hours" lines={[s.hours]} />
            </div>
            <div className="mt-10 overflow-hidden rounded-sm border border-navy/10">
              <iframe
                title="UYI location"
                src={mapSrc}
                className="w-full h-64"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-sm border border-navy/10 bg-white p-8 lg:p-10">
              <h3 className="font-display text-2xl text-navy mb-6">Request a callback</h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name *">
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-sm" />
                </Field>
                <Field label="Phone *">
                  <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91" className="rounded-sm" />
                </Field>
                <Field label="Email">
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="rounded-sm" />
                </Field>
                <Field label="Service">
                  <select
                    data-testid="contact-service"
                    value={form.service}
                    onChange={set("service")}
                    className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm text-navy"
                  >
                    <option value="">Select a service</option>
                    {SERVICES.map((sv) => (
                      <option key={sv.title} value={sv.title}>{sv.title}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Message">
                  <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your project…" rows={5} className="rounded-sm" />
                </Field>
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="mt-7 inline-flex items-center gap-3 rounded-sm bg-navy px-8 py-4 text-white hover:bg-gold hover:text-navy transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send enquiry"} <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Info({ icon: Icon, title, lines, testid }) {
  return (
    <div className="flex gap-4" data-testid={testid}>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-navy text-gold">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-gold">{title}</div>
        {lines.filter(Boolean).map((l, i) => (
          <div key={i} className="mt-1 text-navy/70 leading-relaxed">{l}</div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">{label}</span>
      {children}
    </label>
  );
}
